import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createPartnershipInquiry } from '@/lib/api';
import { toast } from 'sonner';
import { Handshake, Send, CheckCircle } from 'lucide-react';

const organizationTypes = [
  "Healthcare Provider",
  "Community Organization",
  "Government Agency",
  "Educational Institution",
  "Faith-Based Organization",
  "Nonprofit Organization",
  "Other"
];

const partnershipInterests = [
  "Resource Sharing",
  "Referral Partnership",
  "Community Outreach",
  "Training Collaboration",
  "Volunteer Programs",
  "Financial Support",
  "Other"
];

const PartnerWithUs = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    organization_type: '',
    partnership_interest: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createPartnershipInquiry(formData);
      setSubmitted(true);
      toast.success('Partnership inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-neutral py-20" data-testid="partner-with-us-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 rounded-2xl shadow-sm">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Inquiry Submitted!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for your interest in partnering with us. Our team will review your inquiry and reach out soon.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  organization_name: '',
                  contact_person: '',
                  email: '',
                  phone: '',
                  organization_type: '',
                  partnership_interest: '',
                  message: ''
                });
              }}
              className="bg-brand-orange hover:bg-brand-orange/90 text-white"
            >
              Submit Another Inquiry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="partner-with-us-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
              <Handshake size={32} />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              Partner With Us
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              We believe in the power of collaboration. If your organization shares our commitment to strengthening behavioral health support systems, we'd love to explore partnership opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-brand-neutral">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl shadow-sm">
            <CardContent className="p-8">
              <h2 className="font-heading text-2xl font-bold text-brand-blue mb-6">
                Partnership Inquiry Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    value={formData.organization_name}
                    onChange={(e) => handleChange('organization_name', e.target.value)}
                    required
                    data-testid="org-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_person">Contact Person *</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => handleChange('contact_person', e.target.value)}
                    required
                    data-testid="contact-person-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organization_type">Type of Organization *</Label>
                  <Select 
                    value={formData.organization_type} 
                    onValueChange={(value) => handleChange('organization_type', value)}
                    required
                  >
                    <SelectTrigger data-testid="org-type-select">
                      <SelectValue placeholder="Select organization type" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizationTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="partnership_interest">Partnership Interest *</Label>
                  <Select 
                    value={formData.partnership_interest} 
                    onValueChange={(value) => handleChange('partnership_interest', value)}
                    required
                  >
                    <SelectTrigger data-testid="partnership-interest-select">
                      <SelectValue placeholder="Select area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {partnershipInterests.map((interest) => (
                        <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    rows={5}
                    placeholder="Tell us about your organization and how you'd like to partner with us..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    required
                    data-testid="message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white py-6 text-lg"
                  data-testid="submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Inquiry'}
                  <Send size={20} className="ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PartnerWithUs;
